import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { normalizeOverallTimeStatus } from '../utils/assessmentTiming';

const formatRemaining = (seconds) => {
  const safe = Math.max(0, Number.parseInt(seconds, 10) || 0);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
    : `${minutes}:${String(remainder).padStart(2, '0')}`;
};

export default function OverallAssessmentTimer({ performanceId, timing, onExpire }) {
  const [status, setStatus] = useState(() => normalizeOverallTimeStatus(timing));
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);
  const expirationInFlight = useRef(false);

  useEffect(() => {
    setStatus(normalizeOverallTimeStatus(timing));
    expirationInFlight.current = false;
    setIsFinalizing(false);
    setError('');
  }, [performanceId, timing?.deadlineAt, timing?.limitSeconds]);

  const synchronize = useCallback(async () => {
    if (!performanceId || status.limitSeconds <= 0) return;
    try {
      const current = normalizeOverallTimeStatus(
        await api.getPerformanceTimeStatus(performanceId),
        status.limitSeconds
      );
      setStatus(current);
      setError('');
    } catch (syncError) {
      console.warn('[OverallAssessmentTimer] Server time synchronization delayed:', syncError);
    }
  }, [performanceId, status.limitSeconds]);

  useEffect(() => {
    if (!performanceId || status.limitSeconds <= 0 || isFinalizing) return undefined;
    const tick = window.setInterval(() => {
      setStatus(previous => ({
        ...previous,
        remainingSeconds: Math.max(0, previous.remainingSeconds - 1),
        expired: previous.remainingSeconds <= 1
      }));
    }, 1000);
    const serverSync = window.setInterval(synchronize, 30000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') synchronize();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    synchronize();
    return () => {
      window.clearInterval(tick);
      window.clearInterval(serverSync);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [performanceId, status.limitSeconds, isFinalizing, synchronize]);

  useEffect(() => {
    if (status.limitSeconds <= 0 || status.remainingSeconds > 0 || expirationInFlight.current) {
      return undefined;
    }
    let cancelled = false;
    let retryId;
    expirationInFlight.current = true;
    setIsFinalizing(true);
    setError('');
    Promise.resolve(onExpire?.())
      .catch(expirationError => {
        if (cancelled) return;
        console.error('[OverallAssessmentTimer] Expiration finalization delayed:', expirationError);
        expirationInFlight.current = false;
        setIsFinalizing(false);
        setError('Connection interrupted. Your saved work is safe; submitting again…');
        retryId = window.setTimeout(() => setRetryToken(value => value + 1), 5000);
      });
    return () => {
      cancelled = true;
      if (retryId) window.clearTimeout(retryId);
    };
  }, [status.limitSeconds, status.remainingSeconds, onExpire, retryToken]);

  if (status.limitSeconds <= 0) return null;
  const remaining = status.remainingSeconds;
  const urgent = remaining <= 60;
  const warning = remaining <= 300;
  const background = urgent ? '#b91c1c' : warning ? '#b45309' : '#0f766e';

  return (
    <div role="timer" aria-live={warning ? 'polite' : 'off'} style={{
      display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
      minWidth: '132px', padding: '7px 12px', borderRadius: '10px',
      background, color: '#fff', boxShadow: '0 2px 6px rgba(15,23,42,.22)'
    }}>
      <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>
        Overall time
      </span>
      <strong style={{ fontSize: '20px', fontVariantNumeric: 'tabular-nums' }}>
        {isFinalizing ? 'Submitting…' : formatRemaining(remaining)}
      </strong>
      {error && <span style={{ maxWidth: '230px', fontSize: '10px', textAlign: 'center' }}>{error}</span>}
    </div>
  );
}

export { formatRemaining };
